import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Tooltip,
  useColorModeValue,
  HStack,
  Badge,
} from "@chakra-ui/react";
import { useSetRecoilState } from "recoil";
import useAxios from "axios-hooks";
import {
  RiDownloadCloud2Line,
  RiFileInfoLine,
  RiTimeLine,
  RiCalendarLine,
  RiRefreshLine,
  RiTerminalBoxLine,
} from "react-icons/ri";
import { FaCalendarAlt, FaClock, FaInfoCircle } from "react-icons/fa";
import { motion } from "framer-motion";
import { keyframes, css } from "@emotion/react";

import Log from "components/log/Log";
import Pulsing from "components/feedback/Pulsing";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const Logs = ({ point }) => {
  const setToast = useSetRecoilState(toastState);
  const [ocppLogs, setOcppLogs] = useState([]);
  const [autoFetchLogs, setAutoFetchLogs] = useState(true);
  const [page, setPage] = useState(1);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const scrollContainerRef = useRef();
  const [endReached, setEndReached] = useState(false);
  const [fromDate, setFromDate] = useState(false);
  const [toDate, setToDate] = useState(false);
  const progressAnimation = keyframes`
  100% {
    background-size: 100% 100%;
  }
`;

  const blinkAnimation = keyframes`
    0% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.4;
    }
  `;
  const [{ data, loading, error }, getLog] = useAxios(
    {
      method: "GET",
      url: `/api/ocppLogs/`,
    },
    { manual: true }
  );

  const [
    { data: downloadedData, loading: downloadLoading, error: downloadError },
    downloadLogs,
  ] = useAxios(
    {
      method: "GET",
      url: `/api/ocppLogs/download`,
      responseType: "blob",
    },
    { manual: true }
  );

  const getLogs = async () => {
    let params = { chargePointCode: point.StationId, page: page };
    if (startTime) {
      params["startTime"] = startTime;
    }
    if (endTime) {
      params["endTime"] = endTime;
    }
    try {
      const response = await getLog({ params });

      setOcppLogs((prev) => {
        if (page === 1) {
          return response.data.data.data;
        } else if (page > 1) {
          if (response.data.data.data.length > 0) {
            return [...prev, ...response.data.data.data];
          } else {
            setEndReached(true);
            return [...prev];
          }
        }
        return prev;
      });
      
      // If the API returns empty data, we've reached the end
      if (response.data.data.data.length === 0) {
        setEndReached(true);
      }
    } catch (error) {
      logger.log(error);
      // setToast({
      //   isOpen: true,
      //   title: "Error occured while getting logs",
      //   status: "error",
      // });
    }
  };

  function calculateDaysDifference(date1, date2) {
    // Convert both dates to milliseconds
    const date1InMs = new Date(date1).getTime();
    const date2InMs = new Date(date2).getTime();

    // Calculate the difference in milliseconds
    const differenceInMs = Math.abs(date2InMs - date1InMs);

    // Convert the difference to days
    const differenceInDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));

    return differenceInDays;
  }

  const logsDownloadHandler = async () => {
    if (!startTime || !endTime) {
      setToast({
        isOpen: true,
        title: "Please select both From and To dates",
        status: "error",
      });
      return;
    }
    if (calculateDaysDifference(startTime, endTime) - 1 > 31) {
      setToast({
        isOpen: true,
        title: "Please select date range less than 31 days",
        status: "error",
      });
      return;
    }
    let params = { chargePointCode: point.StationId };
    if (startTime) {
      params["startTime"] = startTime;
    }
    if (endTime) {
      params["endTime"] = endTime;
    }
    try {
      const response = await downloadLogs({ params });
      // Create a blob object from the response data
      const blob = new Blob([response.data], { type: "application/json" });

      // Create a temporary anchor element to trigger the download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.headers["content-disposition"].split("=")[1]; // Extract the filename from the Content-Disposition header
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error("Error downloading logs:", error);
      setToast({
        isOpen: true,
        title: "Error occured while getting logs",
        status: "error",
      });
    }
  };

  const logs = useMemo(() => {
    if (ocppLogs?.length > 0) {
      return ocppLogs.map((ele, index) => {
        return <Log key={index} data={ele} />;
      });
    } else {
      return (
        <Text color={"white"} textAlign={"center"}>
          No Logs Present
        </Text>
      );
    }
  }, [ocppLogs]);

  const dateChangeHandler = (event, type) => {
    if (type === "startTime") {
      setStartTime(event.target.value);
      setFromDate(true);
    }
    if (type === "endTime") {
      setEndTime(event.target.value);
      setToDate(true);
    }
    setEndReached(false);
    setAutoFetchLogs(false);
    setPage(1);
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    const handleScroll = () => {
      const isAtTop = scrollContainer.scrollTop === 0;
      if (isAtTop && !startTime && !endTime) {
        setPage(1);
        setAutoFetchLogs(true);
      } else {
        if (autoFetchLogs) {
          setAutoFetchLogs(false);
        }
      }
      
      // Check if we've scrolled to the bottom (with a small buffer of 5px)
      const isAtBottom = 
        Math.abs(scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop) < 5;
        
      if (isAtBottom && !endReached && !loading) {
        setPage((prev) => prev + 1);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [ocppLogs, endReached, autoFetchLogs, startTime, endTime, loading]);

  useEffect(() => {
    setEndReached(false);
    if (page === 1) {
      getLogs();
    }
  }, [startTime, endTime]);

  useEffect(() => {
    if (page > 1 && !endReached) {
      getLogs();
    }
  }, [page]);

  useEffect(() => {
    // Set up an interval to run the function every 10 seconds
    if (page === 1) {
      getLogs();
    }
    const intervalId = setInterval(() => {
      if (autoFetchLogs && page === 1) {
        getLogs();
      }
    }, 5000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [autoFetchLogs]);

  const themeColor = "#23A979";
  const inputBgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const logBgColor = useColorModeValue("gray.900", "black");

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <GlassMorphismCard mb={6}>
        {/* Header */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex justify="space-between" align="center" mb={2}>
            <Heading size="md" color="gray.700">
              <Icon as={RiTerminalBoxLine} mr={2} color={themeColor} />
              OCPP Logs
            </Heading>
            <HStack spacing={2}>
              {autoFetchLogs && (
                <Flex
                  bg="green.500"
                  px={3}
                  py={1}
                  borderRadius="full"
                  align="center"
                  justifyContent="center"
                >
                  <Box 
                    w="6px" 
                    h="6px" 
                    borderRadius="full" 
                    bg="white"
                    mr={1.5}
                    css={{
                      animation: `${blinkAnimation} 1.5s infinite`,
                    }}
                  />
                  <Text fontSize="xs" fontWeight="bold" color="white">
                    LIVE
                  </Text>
                </Flex>
              )}
              <Text fontSize="sm" color="gray.500">
                {point?.StationId
                  ? `Station ID: ${point.StationId}`
                  : "No station selected"}
              </Text>
            </HStack>
          </Flex>
          <Text fontSize="sm" color="gray.500" mb={4}>
            View and download communication logs between the charge point and
            server
          </Text>
        </Box>

        {/* Filter Controls */}
        <Flex
          p={6}
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "flex-end" }}
          gap={4}
          position="relative"
        >
          <FormControl flex="1">
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
              <Flex align="center">
                <Icon as={FaCalendarAlt} mr={2} color={themeColor} />
                From Date
              </Flex>
            </FormLabel>
            <InputGroup>
              <Input
                type="datetime-local"
                bg={inputBgColor}
                borderRadius="md"
                borderColor={borderColor}
                focusBorderColor={themeColor}
                fontSize="sm"
                boxShadow="sm"
                value={startTime || ""}
                onChange={(e) => dateChangeHandler(e, "startTime")}
                _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                _hover={{ borderColor: themeColor }}
              />
            </InputGroup>
          </FormControl>

          <FormControl flex="1">
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
              <Flex align="center">
                <Icon as={FaCalendarAlt} mr={2} color={themeColor} />
                To Date
              </Flex>
            </FormLabel>
            <InputGroup>
              <Input
                type="datetime-local"
                bg={inputBgColor}
                borderRadius="md"
                borderColor={borderColor}
                focusBorderColor={themeColor}
                fontSize="sm"
                boxShadow="sm"
                value={endTime || ""}
                onChange={(e) => dateChangeHandler(e, "endTime")}
                _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                _hover={{ borderColor: themeColor }}
              />
            </InputGroup>
          </FormControl>

          <Flex gap={3} alignSelf={{ base: "flex-end", md: "flex-end" }}>
            <Tooltip
              label="Download logs in this date range"
              placement="top"
              hasArrow
            >
              <Button
                leftIcon={<RiDownloadCloud2Line size={16} />}
                bg={themeColor}
                color="white"
                _hover={{
                  bg: `${themeColor}90`,
                  transform: "translateY(-2px)",
                }}
                _active={{ bg: `${themeColor}d0` }}
                borderRadius="md"
                onClick={logsDownloadHandler}
                isLoading={downloadLoading}
                loadingText="Downloading"
                isDisabled={ocppLogs?.length === 0 || (!startTime && !endTime)}
                transition="all 0.2s"
                boxShadow="md"
              >
                Download
              </Button>
            </Tooltip>

            <Tooltip
              label="Maximum allowed time frame is 31 Days"
              placement="top"
              hasArrow
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <Icon as={FaInfoCircle} color="gray.400" boxSize={5} />
              </Box>
            </Tooltip>
          </Flex>
        </Flex>
      </GlassMorphismCard>

      {/* Logs Display */}
      <GlassMorphismCard p={0} overflow="hidden">
        <Box
          bg={logBgColor}
          height="750px"
          overflowY="auto"
          p={4}
          ref={scrollContainerRef}
          position="relative"
          css={{
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0, 0, 0, 0.1)",
            },
            "&::-webkit-scrollbar-thumb": {
              background: themeColor,
              borderRadius: "4px",
            },
          }}
        >
          {logs}

          {loading && (
            <Box
              position="sticky"
              bottom={0}
              width="100%"
              height="4px"
              bg={`linear-gradient(90deg, ${themeColor} 0%, #474bff 100%) left/0% 100% no-repeat transparent`}
              css={{
                animation: `${progressAnimation} 2s infinite linear`,
              }}
            />
          )}
        </Box>
      </GlassMorphismCard>
    </MotionBox>
  );
};

export default Logs;
